import { EpicAuthClient } from '@/features/auth/services/EpicAuthClient'
import {
  fetchGuardianContacts,
  type GuardianContactItem,
  type GuardianContactTelecomItem,
} from '@/features/auth/services/guardianContactApi'
import { isGuardianRelationshipValue, type CanonicalGuardianContact } from '@/ordering/canonicalOrder'
import * as Sentry from '@sentry/react'

export type GuardianContactLookupOutcome =
  | { status: 'found'; guardian: CanonicalGuardianContact; totalCount: number }
  /** Patient has no legal guardian on file — a normal result, not an error. */
  | { status: 'none' }
  | { status: 'not_configured' }
  | { status: 'error'; message: string }

/** True when the Epic guardian-contact lookup can run (non-empty state and stored Epic session). */
export function isEpicGuardianContactLookupAvailable(epicState: string | undefined): boolean {
  const key = epicState?.trim() ?? ''
  if (!key) {
    return false
  }
  return EpicAuthClient.getInstance().hasSession(key)
}

/**
 * Kinship / FHIR relationship text → guardian UPPER_SNAKE relationship value.
 * Handles the common English kinship labels the endpoint surfaces (e.g.
 * "Mother", "Father", "Grandmother") and validates the result against
 * {@link isGuardianRelationshipValue}. Falls back to `LEGAL_GUARDIAN` when the
 * contact is a legal guardian with no usable kinship, else `OTHER`. Never
 * returns an unsupported literal.
 */
function mapKinshipToGuardianRelationship(relationship: string | null | undefined, isLegalGuardian: boolean): string {
  const raw = (relationship ?? '').trim()
  if (raw) {
    // Direct table for the common kinship labels (title-case English from FHIR).
    const KINSHIP_MAP: Record<string, string> = {
      MOTHER: 'MOTHER',
      MOM: 'MOTHER',
      FATHER: 'FATHER',
      DAD: 'FATHER',
      SIBLING: 'SIBLING',
      BROTHER: 'SIBLING',
      SISTER: 'SIBLING',
    }
    const upper = raw.toUpperCase()
    const mapped = KINSHIP_MAP[upper] ?? upper
    if (isGuardianRelationshipValue(mapped)) {
      return mapped
    }
  }
  return isLegalGuardian ? 'LEGAL_GUARDIAN' : 'OTHER'
}

/**
 * Split a FHIR contact display name into first / last. FHIR `name.text` for a
 * contact arrives in one of two shapes, and provider-api forwards only this
 * flattened string (no structured `name.given[]` / `name.family`), so we must
 * recover the parts here.
 *
 *   1. Comma form — `"Family,Given Middle..."` (family before the comma),
 *      e.g. `"RCIGM,PILOTTWNTYSIX MOM"` or `"Smith,Mary Anne"`. The part before
 *      the comma is the **full** last name (so hyphenated / multi-word surnames
 *      like `"Van Houten"` are preserved); **everything** after the comma is the
 *      first name. We deliberately keep every post-comma token — including a
 *      possible middle name or a trailing role token (`"MOM"`) — so the stored
 *      demographic matches the EHR chart verbatim rather than silently dropping
 *      sub-parts (which previously corrupted compound/middle given names).
 *
 *   2. Space form — `"Given Middle... Family"` (no comma). The **last**
 *      whitespace token is the family name and everything before it is the first
 *      name. Splitting on the LAST space (not the first) keeps a middle name on
 *      the first-name side (`"Mary Anne Smith"` → first `"Mary Anne"`, last
 *      `"Smith"`) instead of absorbing it into the family term, which previously
 *      broke the Epic `family=` MRN search.
 *
 * Returns `null` fields when unresolvable (empty input, or a single token with
 * no separator — treated as a lone first name).
 */
function splitContactName(name: string | null | undefined): {
  firstName: string | null
  lastName: string | null
} {
  const t = (name ?? '').trim()
  if (!t) return { firstName: null, lastName: null }
  const comma = t.indexOf(',')
  if (comma !== -1) {
    const last = t.slice(0, comma).trim()
    // Keep the full post-comma remainder as the first name (collapse internal
    // whitespace), preserving middle names and any trailing tokens.
    const first =
      t
        .slice(comma + 1)
        .trim()
        .replace(/\s+/g, ' ') || null
    return { firstName: first, lastName: last || null }
  }
  // No comma: split on the LAST space so the final token is the family name and
  // any middle name stays with the first name.
  const lastSpace = t.lastIndexOf(' ')
  if (lastSpace === -1) return { firstName: t, lastName: null }
  return {
    firstName: t.slice(0, lastSpace).trim().replace(/\s+/g, ' ') || null,
    lastName: t.slice(lastSpace + 1).trim() || null,
  }
}

function telecomEmailPhone(telecom: GuardianContactTelecomItem[]): {
  email: string | null
  phone: string | null
} {
  let email: string | null = null
  let phone: string | null = null
  for (const t of telecom) {
    const sys = (t.system ?? '').toLowerCase()
    const val = typeof t.value === 'string' ? t.value.trim() : ''
    if (!val) continue
    if (!email && (sys === 'email' || sys === 'mailto')) {
      email = val
    }
    if (!phone && (sys === 'phone' || sys === 'sms')) {
      phone = val
    }
  }
  return { email, phone }
}

/** Map one FHIR guardian contact into the canonical guardian shape. */
function mapGuardianItemToCanonical(item: GuardianContactItem): CanonicalGuardianContact {
  const { firstName, lastName } = splitContactName(item.name)
  const { email, phone } = telecomEmailPhone(item.telecom ?? [])
  return {
    contactNotAvailable: false,
    firstName,
    lastName,
    dateOfBirth: null,
    email,
    phone,
    relationshipToPatient: mapKinshipToGuardianRelationship(item.relationship, item.isLegalGuardian === true),
  }
}

/** True when a mapped guardian carries at least one substantive field. */
function guardianHasContent(g: CanonicalGuardianContact): boolean {
  return [g.firstName, g.lastName, g.email, g.phone].some((v) => v != null && String(v).trim() !== '')
}

/**
 * Fetch the proband's legal guardian contact from Epic FHIR via
 * POST /fhir/patient/guardian-contact and map it into a {@link CanonicalGuardianContact}.
 * Requires an Epic SMART session (`epicState`).
 *
 * The endpoint may return multiple legal guardians; this returns the first one
 * that carries usable content (with `totalCount` for observability). A patient
 * with no legal guardian resolves to `{ status: 'none' }` (normal, not error).
 */
export async function lookupGuardianContact(
  epicState: string | undefined,
  signal?: AbortSignal
): Promise<GuardianContactLookupOutcome> {
  const state = epicState?.trim() ?? ''
  if (!state || !EpicAuthClient.getInstance().hasSession(state)) {
    return { status: 'not_configured' }
  }

  return Sentry.startSpan(
    {
      op: 'http.client',
      name: 'POST /fhir/patient/guardian-contact',
    },
    async (span) => {
      let items: GuardianContactItem[] | null
      try {
        items = await fetchGuardianContacts(state, signal)
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') {
          throw err
        }
        return { status: 'error', message: 'Guardian contact lookup failed. Try again later.' }
      }

      if (items === null) {
        return { status: 'error', message: 'Guardian contact lookup failed. Try again later.' }
      }

      span.setAttribute('guardian_count', items.length)

      if (items.length === 0) {
        return { status: 'none' }
      }

      // Prefer the first guardian that has usable content; otherwise fall back
      // to the first item so a relationship-only guardian still surfaces.
      const mappedAll = items.map((it) => mapGuardianItemToCanonical(it))
      const guardian = mappedAll.find((g) => guardianHasContent(g)) ?? mappedAll[0]

      return { status: 'found', guardian, totalCount: items.length }
    }
  )
}
